import React from 'react';
import PropTypes from 'prop-types';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { WeezSocket } from "@weezevent/piglet";
import Loader from '../../loader';

const FetchContext = React.createContext();

export const useFetch = () => React.useContext(FetchContext);

// Check if we're running inside an iframe
export const inIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

export const hasFragmentPreview = () => {
    const _fragments = window.location.hash.split("#");
    return _fragments.find(fragment => fragment === 'weez_preview');
}

const SigninRedirect = () => {
    const auth = useAuth();
    React.useEffect(() => {
        // Include full path with hash fragment so we can restore it after redirect
        const fullPath = window.location.pathname + window.location.search + window.location.hash;
        auth.signinRedirect({
            url_state: fullPath
        });
    }, [auth]);
    return null;
};

// Component that handles iframe token request from parent (willy)
function IframeTokenProvider({ children }) {
    const [token, setToken] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        WeezSocket.listen();
        
        WeezSocket.send(
            { type: "request_token", origin: "neo", payload: {} },
            { needs_answer: true }
        ).then(msg => {
            setToken(msg.payload.token);
            setIsLoading(false);
        }).catch(err => {
            setError(err);
            setIsLoading(false);
        });
    }, []);

    if (isLoading) {
        return <Loader />;
    }

    if (error || !token) {
        return <Loader />;
    }

    // Create a fetch alias that injects the parent's token
    const fetch = (url, opts) => {
        opts = opts || {};
        const headers = (opts.headers = opts.headers || {});
        headers.Authorization = `Bearer ${token}`;
        return window.fetch(url, opts);
    };

    return <FetchContext.Provider value={fetch}>{children}</FetchContext.Provider>;
}

IframeTokenProvider.propTypes = {
    children: PropTypes.node.isRequired
};

function NeoConnectInner({ children, requireAuth = false, useIframeToken = false }) {
    const auth = useAuth();

    // In iframe mode, use IframeTokenProvider for the fetch context
    if (useIframeToken) {
        return <IframeTokenProvider>{children}</IframeTokenProvider>;
    }

    if (auth.isLoading) {
        return <Loader />;
    }

    // Ignore "No matching state found in storage" error - this just means 
    // there's no OIDC callback to process, which is normal for direct page loads
    if (auth.error && auth.error.message !== 'No matching state found in storage') {
        return <Loader />;
    }

    if (auth.isAuthenticated) {
        // Create a fetch alias that injects the token
        const fetch = (url, opts) => {
            opts = opts || {};
            const headers = (opts.headers = opts.headers || {});
            headers.Authorization = `${auth.user.token_type} ${auth.user.access_token}`;
            return window.fetch(url, opts);
        };

        return <FetchContext.Provider value={fetch}>{children}</FetchContext.Provider>;
    }

    if (requireAuth) {
        return <SigninRedirect />;
    }

    return <FetchContext.Provider value={window.fetch}>{children}</FetchContext.Provider>;
}

NeoConnectInner.propTypes = {
    children: PropTypes.node.isRequired,
    requireAuth: PropTypes.bool,
    useIframeToken: PropTypes.bool
};

export default function NeoConnect({ children }) {
    const { OIDCUrl, clientId } = window.weezConfig.connect;
    const isPreview = hasFragmentPreview();
    const isIframePreview = inIframe() && isPreview;

    const onSigninCallback = user => {
        const dest = user.url_state || '/ticket/';
        // Navigate to the restored URL (with hash fragment preserved)
        // Use location.replace to trigger a full navigation since we're outside Router context
        window.location.replace(dest);
    };
    
    return (
        <AuthProvider
            authority={OIDCUrl}
            client_id={clientId}
            redirect_uri={`${window.location.origin}/ticket/`}
            automaticSilentRenew
            onSigninCallback={onSigninCallback}
        >
            <NeoConnectInner 
                requireAuth={!isIframePreview && Boolean(isPreview)}
                useIframeToken={isIframePreview}
            >
                {children}
            </NeoConnectInner>
        </AuthProvider>
    );
}

NeoConnect.propTypes = {
    children: PropTypes.node.isRequired,
};
